import { createAsyncThunk, createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import API, { typeThrownException } from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	TelematicsDriverTripsClient,
	TripSummaryDto,
	TripSummaryDtoPaginatedList,
} from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';

interface TelematicsTripsState {
	items: TripSummaryDto[];
	pageNumber: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
}

const initialState: TelematicsTripsState = {
	items: [],
	pageNumber: 1,
	pageSize: 5,
	totalCount: 0,
	totalPages: 0,
	hasPreviousPage: false,
	hasNextPage: false,
	loading: false,
	loadingMore: false,
	error: null,
};

interface FetchTripsParams {
	policyReference: string;
	pageNumber?: number;
	pageSize?: number;
}

export const fetchTrips = createAsyncThunk<TripSummaryDtoPaginatedList, FetchTripsParams, { rejectValue: unknown }>(
	'telematicsTrips/fetchTrips',
	async (params: FetchTripsParams, { rejectWithValue }) => {
		try {
			const client = new TelematicsDriverTripsClient(undefined, API);
			const response = await client.trips(params.policyReference, params.pageNumber ?? 1, params.pageSize ?? 5);
			return response;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const fetchMoreTrips = createAsyncThunk<TripSummaryDtoPaginatedList, FetchTripsParams, { rejectValue: unknown }>(
	'telematicsTrips/fetchMoreTrips',
	async (params: FetchTripsParams, { rejectWithValue }) => {
		try {
			const client = new TelematicsDriverTripsClient(undefined, API);
			const response = await client.trips(params.policyReference, params.pageNumber, params.pageSize ?? 5);
			return response;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const telematicsTripsSlice = createSlice({
	name: 'telematicsTrips',
	initialState,
	reducers: {
		setTrips: (state: TelematicsTripsState, action: PayloadAction<TripSummaryDtoPaginatedList>) => {
			state.items = action.payload.items ?? [];
			state.pageNumber = action.payload.pageNumber ?? 1;
			state.pageSize = action.payload.pageSize ?? 5;
			state.totalCount = action.payload.totalCount ?? 0;
			state.totalPages = action.payload.totalPages ?? 0;
			state.hasPreviousPage = action.payload.hasPreviousPage ?? false;
			state.hasNextPage = action.payload.hasNextPage ?? false;
		},
		clearTrips: () => initialState,
	},
	extraReducers: (builder: ActionReducerMapBuilder<TelematicsTripsState>) => {
		// Initial fetch
		builder.addCase(fetchTrips.pending, (state: TelematicsTripsState) => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(fetchTrips.fulfilled, (state: TelematicsTripsState, { payload }) => {
			state.loading = false;
			state.items = payload.items ?? [];
			state.pageNumber = payload.pageNumber ?? 1;
			state.pageSize = payload.pageSize ?? 5;
			state.totalCount = payload.totalCount ?? 0;
			state.totalPages = payload.totalPages ?? 0;
			state.hasPreviousPage = payload.hasPreviousPage ?? false;
			state.hasNextPage = payload.hasNextPage ?? false;
		});
		builder.addCase(fetchTrips.rejected, (state: TelematicsTripsState, action) => {
			state.loading = false;
			state.error = action.error.message ?? 'Failed to fetch trips';
		});

		// Fetch more (infinite scroll)
		builder.addCase(fetchMoreTrips.pending, (state: TelematicsTripsState) => {
			state.loadingMore = true;
			state.error = null;
		});
		builder.addCase(fetchMoreTrips.fulfilled, (state: TelematicsTripsState, { payload }) => {
			state.loadingMore = false;
			// Append new items to existing items
			state.items = [...state.items, ...(payload.items ?? [])];
			state.pageNumber = payload.pageNumber ?? state.pageNumber;
			state.pageSize = payload.pageSize ?? 5;
			state.totalCount = payload.totalCount ?? 0;
			state.totalPages = payload.totalPages ?? 0;
			state.hasPreviousPage = payload.hasPreviousPage ?? false;
			state.hasNextPage = payload.hasNextPage ?? false;
		});
		builder.addCase(fetchMoreTrips.rejected, (state: TelematicsTripsState, action) => {
			state.loadingMore = false;
			state.error = action.error.message ?? 'Failed to fetch more trips';
		});
	},
});

export const { setTrips, clearTrips } = telematicsTripsSlice.actions;
export default telematicsTripsSlice.reducer;
